import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from '@plato/ui/components/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@plato/ui/containers/dropdown-menu';
import { MenuItemLabel, MenuItemLeading } from '@plato/ui/primitives/menu';
import { cn } from '@plato/ui/util/classes';
import { isEqual } from 'lodash-es';
import { ChevronDownIcon, EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button.tsx';
import type { QuickFilter } from './lib/filter-utils.ts';
import type { UiFilterVisibility } from './types/ui-filter-visibility.ts';

export type SavedFilterView<T> = {
    id: string;
    name: string;
    visibility: UiFilterVisibility;
    filters: T;
    isOwn: boolean;
};

type SavedFilterViewListProps<T> = {
    views: SavedFilterView<T>[];
    isLoading?: boolean;
    currentlyActiveFilters: T;
    onApplyFilterView: (filters: T, appliedView?: SavedFilterView<T>) => void;
    onEditFilterView?: (viewId: string) => void;
    onDeleteFilterView?: (viewId: string) => void;
    quickFilters?: QuickFilter<T>[];
};

function filterViewIsMatchedToCurrentFilters<T>(filterViewFilters: T, currentlyActiveFilters: T): boolean {
    return isEqual(filterViewFilters, currentlyActiveFilters);
}

export function SavedFilterViewList<T>({
    views,
    isLoading,
    currentlyActiveFilters,
    onApplyFilterView,
    onEditFilterView,
    onDeleteFilterView,
    quickFilters,
}: SavedFilterViewListProps<T>) {
    const { t } = useTranslation('filters');
    const [open, setOpen] = React.useState(false);
    const [contextMenuOpen, setContextMenuOpen] = React.useState<string | null>(null);

    const selectedFilterView = views.find((view) =>
        filterViewIsMatchedToCurrentFilters(view.filters, currentlyActiveFilters),
    );

    const myViews = views.filter((view) => view.isOwn);
    const sharedViews = views.filter((view) => !view.isOwn);

    const sharedItems = [
        ...sharedViews.map((view) => ({ id: view.id, name: view.name })),
        ...(quickFilters?.map((qf) => ({ id: qf.id, name: qf.label })) || []),
    ];

    const handleEdit = (viewId: string) => {
        setOpen(false);
        setContextMenuOpen(null);

        onEditFilterView?.(viewId);
    };

    const handleDelete = (viewId: string) => {
        setOpen(false);
        setContextMenuOpen(null);

        onDeleteFilterView?.(viewId);
    };

    const handleValueChange = (value: string | undefined) => {
        // Check if it's a saved view first
        const selectedView = views.find((view) => view.id === value);
        if (selectedView) {
            onApplyFilterView(selectedView.filters, selectedView);
        } else {
            // If not a saved view, check if it's a quick filter
            const selectedQuickFilter = quickFilters?.find((qf) => qf.id === value);
            if (selectedQuickFilter) {
                onApplyFilterView(selectedQuickFilter.filter);
            }
        }
    };

    return (
        <Select
            title={t('saved_views__all_views')}
            open={open}
            onOpenChange={setOpen}
            value={selectedFilterView?.id}
            onValueChange={handleValueChange}
        >
            <SelectTrigger
                className={cn('w-auto')}
                render={
                    <Button
                        variant="quick-filter"
                        rightIcon={ChevronDownIcon}
                        rightIconProps={{
                            rotateOnActive: true,
                        }}
                        selected={Boolean(selectedFilterView?.id)}
                        isLoading={isLoading}
                    />
                }
            >
                {selectedFilterView?.name || t('saved_views__all_views')}
            </SelectTrigger>
            <SelectContent className="sm:max-w-80">
                {myViews.length > 0 && (
                    <SelectGroup label={t('saved_views__my_views')}>
                        {myViews.map((view) => (
                            // The option renders a `<li>` around its button, so the row
                            // itself is what has to stretch.
                            <div key={view.id} className="relative flex items-center group [&>li]:flex-1">
                                <SelectItem value={{ value: view.id, label: view.name }} />
                                <DropdownMenu
                                    open={contextMenuOpen === view.id}
                                    onOpenChange={(isOpen) => {
                                        setContextMenuOpen(isOpen ? view.id : null);
                                    }}
                                >
                                    <DropdownMenuTrigger
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                onKeyDown={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                rightIcon={EllipsisVerticalIcon}
                                            />
                                        }
                                    />
                                    <DropdownMenuContent align="end" side="bottom">
                                        <DropdownMenuItem
                                            onClick={() => {
                                                handleEdit(view.id);
                                            }}
                                        >
                                            <MenuItemLeading className="text-inherit">
                                                <PencilIcon />
                                            </MenuItemLeading>
                                            <MenuItemLabel>{t('saved_views__edit')}</MenuItemLabel>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => {
                                                handleDelete(view.id);
                                            }}
                                            className="text-danger"
                                        >
                                            <MenuItemLeading className="text-danger">
                                                <Trash2Icon />
                                            </MenuItemLeading>
                                            <MenuItemLabel>{t('saved_views__delete')}</MenuItemLabel>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ))}
                    </SelectGroup>
                )}
                {sharedItems.length > 0 && (
                    <SelectGroup label={t('saved_views__shared_views')}>
                        {sharedItems.map((item) => (
                            <SelectItem key={item.id} value={{ value: item.id, label: item.name }} />
                        ))}
                    </SelectGroup>
                )}
            </SelectContent>
        </Select>
    );
}
